import { createContext, useContext, useEffect, useRef, useState } from "react";
import { FeedObject } from "app/core/common";
import { useHook } from "app/hooks/common";
import * as api from "../api/user.api";

interface FeedState {
  feed: FeedObject[];
  feedLoading: boolean;
  lastUpdated: Date;
  moreExists: boolean;
}

interface FeedActions {
  fetchLatestFeed: () => void;
  fetchMoreFeed: () => void;
  setFeed: (feed: FeedObject[]) => void;
}

type FeedHook = FeedState & FeedActions;

const FeedContext = createContext<FeedHook>({
  feed: [],
  feedLoading: true,
  lastUpdated: new Date(),
  moreExists: true,
  fetchLatestFeed: () => {},
  fetchMoreFeed: () => {},
  setFeed: () => {},
});

interface Props {
  id?: string;
  children?: React.ReactNode;
}

export const FeedContextProvider: React.FC<Props> = ({ children }) => {
  const { cookie, userInfo, followingList, followingListLoaded } = useHook();
  const [feed, setFeed] = useState<FeedObject[]>([]);
  const [feedLoading, setFeedLoading] = useState<boolean>(true);
  const [feedMeta, setFeedMeta] = useState<any>();
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [page, setPage] = useState(1);
  const [moreExists, setMoreExists] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Get ids of users to fetch feed for and put current user at the top
  const ids = [
    userInfo?.id,
    ...followingList.map((item) => item.follower_id.id),
  ];

  /**************************************/
  // Fetch User Feed
  /**************************************/
  const fetchUserFeed = async () => {
    setFeedLoading(true);

    if (userInfo && cookie && followingListLoaded) {
      try {
        const newFeed = await api.FetchFeed({ userInfo, ids, page }); // Fetch feed
        setFeed((prevFeed) => [...prevFeed, ...newFeed.feed]); // Append new feed to existing feed
        setFeedMeta(newFeed.meta);
        setFeedLoading(false);
        setLastUpdated(new Date());
      } catch (error) {
        console.error(error); // Log error
        setFeedLoading(false);
      }
    }
  };

  // Fetch feed on mount
  useEffect(() => {
    fetchUserFeed();
  }, [userInfo, cookie, followingListLoaded, page]);


/*   useEffect(() => {
    if (followingList) {
      console.log(followingList);
    }
  }, [followingList]); */

  /**************************************/
  // Fetch More Feed
  /**************************************/
  const fetchMoreFeed = () => {
    const totalPages = feed.length > 0 ? Math.ceil(feedMeta.filter_count / feedMeta.limit) : undefined;
    if (totalPages != undefined && page !== totalPages && !feedLoading) {
      setFeedLoading(true);
      setPage(page + 1); // Increment page number
      setMoreExists(page + 1 !== totalPages); // Check if more pages exist
    }
  };

  /**************************************/
  // Fetch Latest and push to top of feed
  /**************************************/
  const fetchLatestFeed = async () => {
    try {
      const lastDate = feed[0].date_created;
      if (lastDate && followingListLoaded && followingList.length > 0) {
        setFeedLoading(true);
        if (userInfo && cookie) {
          await api
            .FetchByDate({ cookie, ids, lastDate, userInfo })
            .then((newFeed) => {
              setFeed((prevFeed) => [...newFeed.feed, ...prevFeed]); // Append new feed to existing feed at the top
              setFeedMeta(newFeed.meta);
              setLastUpdated(new Date());
              setFeedLoading(false);
            })
            .catch((error) => {
              setFeedLoading(false);
            });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FeedContext.Provider
      value={{
        feed,
        feedLoading,
        lastUpdated,
        moreExists,
        fetchLatestFeed,
        fetchMoreFeed,
        setFeed,
      }}
    >
      {children}
    </FeedContext.Provider>
  );
};

export const useFeed = (): FeedHook => useContext(FeedContext);
